/**
 * TimerWidget Component
 *
 * Displays an active timer with elapsed time, controls for stopping,
 * and side-switching for breastfeeding.
 * For UPRIGHT timers, displays a countdown instead.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActiveTimer, useTimers, getSideDurations } from '../stores/useTimers';
import { useElapsedTime, formatDuration } from '../hooks/useElapsedTime';

interface TimerWidgetProps {
  timer: ActiveTimer;
  babyName?: string;
  onStop?: (timer: ActiveTimer) => void;
  compact?: boolean;
}

export default function TimerWidget({
  timer,
  babyName,
  onStop,
  compact = false,
}: TimerWidgetProps) {
  const navigate = useNavigate();
  const { stopTimer, switchSide, pauseTimer, resumeTimer, toggleHeadUp, getHeadUpSeconds } = useTimers();
  const { formatted, elapsedSeconds, elapsedMinutes } = useElapsedTime(timer.startTime, {
    totalPausedTime: timer.totalPausedTime,
    isPaused: timer.isPaused,
    pausedAt: timer.pausedAt,
  });

  // TUMMY timer
  const isTummy = timer.type === 'TUMMY';
  // headUpSeconds updates on every render (driven by elapsedSeconds tick)
  const headUpSeconds = isTummy ? getHeadUpSeconds(timer.id) : 0;
  const fmtSecs = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // UPRIGHT countdown logic
  const isUpright = timer.type === 'UPRIGHT';
  const targetSeconds = isUpright && timer.targetDurationMs ? timer.targetDurationMs / 1000 : 0;
  const remainingSeconds = isUpright ? Math.max(0, targetSeconds - elapsedSeconds) : 0;
  const isComplete = isUpright && remainingSeconds <= 0 && elapsedSeconds > 0;
  const progressPercent = isUpright && targetSeconds > 0
    ? Math.min(100, (elapsedSeconds / targetSeconds) * 100)
    : 0;
  const notifiedRef = useRef(false);

  // Format countdown as MM:SS
  const countdownMinutes = Math.floor(remainingSeconds / 60);
  const countdownSecs = Math.floor(remainingSeconds % 60);
  const countdownFormatted = `${countdownMinutes}:${countdownSecs.toString().padStart(2, '0')}`;

  // Auto-notification and auto-dismiss on completion
  useEffect(() => {
    if (!isComplete || notifiedRef.current) return;
    notifiedRef.current = true;

    // Fire browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification("Time's up!", {
        body: `Safe to lay ${babyName || 'baby'} down now`,
        icon: '/icons/icon-192x192.png',
      });
    }

    // Auto-stop after 5 seconds
    const timeout = setTimeout(() => {
      stopTimer(timer.id);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isComplete, babyName, timer.id, stopTimer]);

  const handleStop = async () => {
    const stoppedTimer = await stopTimer(timer.id);
    if (stoppedTimer && onStop) {
      onStop(stoppedTimer);
    }
  };

  const handleStopAndLog = () => {
    if (timer.type === 'TUMMY') {
      // Pass durations as seconds
      const params = new URLSearchParams({
        type: 'TUMMY_TIME',
        fromTimer: timer.id,
        tummySeconds: String(Math.round(elapsedSeconds)),
        headUpSeconds: String(headUpSeconds),
      });
      navigate(`/app/baby/${timer.babyId}/event/add?${params.toString()}`);
      return;
    }
    // Navigate to AddEvent with timer data
    const eventType = timer.type === 'FEED' ? 'FEED' : timer.type === 'KICK_COUNT' ? 'KICK_COUNT' : timer.type === 'CONTRACTION' ? 'CONTRACTION' : 'SLEEP';
    const params = new URLSearchParams({
      type: eventType,
      fromTimer: timer.id,
    });
    navigate(`/app/baby/${timer.babyId}/event/add?${params.toString()}`);
  };

  const handleSwitchSide = async () => {
    await switchSide(timer.id);
  };

  const handlePauseResume = async () => {
    if (timer.isPaused) {
      await resumeTimer(timer.id);
    } else {
      await pauseTimer(timer.id);
    }
  };

  const sideDurations = timer.type === 'FEED' ? getSideDurations(timer) : null;

  // ======== UPRIGHT TIMER COMPACT ========
  if (isUpright && compact) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
        isComplete
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
      }`}>
        <span className="text-lg">{isComplete ? '✅' : '⏳'}</span>
        <span className="font-mono font-semibold">
          {isComplete ? 'Done!' : countdownFormatted}
        </span>
      </div>
    );
  }

  // ======== UPRIGHT TIMER FULL ========
  if (isUpright) {
    return (
      <div className={`rounded-xl p-4 text-white shadow-lg ${
        isComplete
          ? 'bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700'
          : 'bg-gradient-to-r from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{isComplete ? '✅' : '⏳'}</span>
            <div>
              <h3 className="font-semibold">Keep Upright</h3>
              {babyName && (
                <p className="text-sm opacity-80">{babyName}</p>
              )}
            </div>
          </div>
        </div>

        {/* Countdown Display */}
        <div className="text-center my-4">
          {isComplete ? (
            <>
              <div className="text-3xl font-bold">All done!</div>
              <p className="text-sm mt-1 opacity-90">
                Safe to lay {babyName || 'baby'} down now
              </p>
            </>
          ) : (
            <>
              <div className="text-5xl font-mono font-bold tracking-wider">
                {countdownFormatted}
              </div>
              <p className="text-sm mt-1 opacity-80">remaining</p>
            </>
          )}
        </div>

        {/* Progress bar */}
        {!isComplete && (
          <div className="w-full bg-white/20 rounded-full h-2 mb-4">
            <div
              className="bg-white h-2 rounded-full transition-all duration-1000"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleStop}
            className={`flex-1 py-2 px-3 rounded-lg font-semibold transition-colors ${
              isComplete
                ? 'bg-white text-green-600 hover:bg-green-50'
                : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // ======== TUMMY COMPACT ========
  if (isTummy && compact) {
    return (
      <div className="flex items-center gap-2 bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 px-3 py-1.5 rounded-full">
        <span className={timer.isPaused ? 'text-lg' : 'animate-pulse text-lg'}>🏋️</span>
        <span className="font-mono font-semibold">{formatted}</span>
        {timer.headUpActive && <span className="text-xs">↑ Head Up</span>}
        {timer.isPaused && (
          <span className="text-xs uppercase font-semibold bg-amber-400 dark:bg-amber-500 text-amber-900 px-1.5 py-0.5 rounded">PAUSED</span>
        )}
      </div>
    );
  }

  // ======== TUMMY FULL ========
  if (isTummy) {
    return (
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl animate-pulse">🏋️</span>
            <div>
              <h3 className="font-semibold">Tummy Time</h3>
              {babyName && <p className="text-sm opacity-80">{babyName}</p>}
            </div>
          </div>
          {timer.isPaused && (
            <span className="text-xs bg-amber-400 text-amber-900 px-2 py-1 rounded-full font-semibold uppercase">PAUSED</span>
          )}
        </div>

        {/* Dual timer display */}
        <div className="grid grid-cols-2 gap-3 my-4">
          <div className="text-center bg-white/20 rounded-lg py-3">
            <div className="text-3xl font-mono font-bold">{formatted}</div>
            <div className="text-xs mt-1 opacity-80">Total time</div>
          </div>
          <div className={`text-center rounded-lg py-3 ${timer.headUpActive ? 'bg-yellow-400/30 ring-2 ring-yellow-300' : 'bg-white/10'}`}>
            <div className="text-3xl font-mono font-bold">{fmtSecs(headUpSeconds)}</div>
            <div className="text-xs mt-1 opacity-80">{timer.headUpActive ? '↑ Head up' : 'Head up'}</div>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {/* Head-up toggle */}
          <button
            onClick={() => toggleHeadUp(timer.id)}
            className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors ${
              timer.headUpActive
                ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-300'
                : 'bg-white/20 hover:bg-white/30'
            }`}
          >
            {timer.headUpActive ? '↓ Head Down' : '↑ Head Up'}
          </button>
          {/* Pause/Resume */}
          <button
            onClick={handlePauseResume}
            className="bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg font-medium transition-colors"
            title={timer.isPaused ? 'Resume' : 'Pause'}
          >
            {timer.isPaused ? '▶' : '⏸'}
          </button>
          {/* Stop & Log */}
          <button
            onClick={handleStopAndLog}
            className="flex-1 bg-white text-teal-600 hover:bg-teal-50 py-2 px-3 rounded-lg font-semibold transition-colors"
          >
            Stop & Log
          </button>
          {/* Discard */}
          <button
            onClick={handleStop}
            className="bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg transition-colors"
            title="Discard"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  // ======== FEED/SLEEP COMPACT ========
  if (compact) {
    return (
      <div className="flex items-center gap-2 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 px-3 py-1.5 rounded-full">
        <span className={timer.isPaused ? 'text-lg' : 'animate-pulse text-lg'}>
          {timer.type === 'FEED' ? '🍼' : timer.type === 'KICK_COUNT' ? '🦶' : timer.type === 'CONTRACTION' ? '⏱️' : '😴'}
        </span>
        <span className="font-mono font-semibold">{formatted}</span>
        {timer.isPaused && (
          <span className="text-xs uppercase font-semibold bg-amber-400 dark:bg-amber-500 text-amber-900 px-1.5 py-0.5 rounded">
            PAUSED
          </span>
        )}
        {timer.type === 'FEED' && timer.side && !timer.isPaused && (
          <span className="text-xs uppercase opacity-75">
            {timer.side === 'left' ? 'L' : 'R'}
          </span>
        )}
      </div>
    );
  }

  // ======== FEED/SLEEP FULL ========
  return (
    <div className="bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 rounded-xl p-4 text-white shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl animate-pulse">
            {timer.type === 'FEED' ? '🍼' : timer.type === 'KICK_COUNT' ? '🦶' : timer.type === 'CONTRACTION' ? '⏱️' : '😴'}
          </span>
          <div>
            <h3 className="font-semibold">
              {timer.type === 'FEED' ? 'Feeding' : timer.type === 'KICK_COUNT' ? 'Kick Count' : timer.type === 'CONTRACTION' ? 'Contraction' : 'Sleep'} Timer
            </h3>
            {babyName && (
              <p className="text-sm text-primary-100 opacity-80">{babyName}</p>
            )}
          </div>
        </div>
        {timer.type === 'SLEEP' && timer.sleepType && (
          <span className="text-xs bg-white/20 px-2 py-1 rounded-full capitalize">
            {timer.sleepType}
          </span>
        )}
      </div>

      {/* Timer Display */}
      <div className="text-center my-4">
        <div className="text-5xl font-mono font-bold tracking-wider">
          {formatted}
        </div>
        <p className="text-sm text-primary-100 mt-1">
          {formatDuration(elapsedMinutes)} elapsed
          {timer.isPaused && (
            <span className="ml-2 bg-amber-400 dark:bg-amber-500 text-amber-900 px-2 py-0.5 rounded-full text-xs font-semibold uppercase">
              PAUSED
            </span>
          )}
        </p>
      </div>

      {/* Side Info for Breastfeeding */}
      {timer.type === 'FEED' && sideDurations && (
        <div className="flex justify-center gap-4 mb-4">
          <div
            className={`text-center px-4 py-2 rounded-lg ${
              timer.side === 'left'
                ? 'bg-white/30 ring-2 ring-white'
                : 'bg-white/10'
            }`}
          >
            <div className="text-xs uppercase opacity-75">Left</div>
            <div className="font-semibold">
              {sideDurations.left > 0 ? `${sideDurations.left}m` : '--'}
            </div>
          </div>
          <div
            className={`text-center px-4 py-2 rounded-lg ${
              timer.side === 'right'
                ? 'bg-white/30 ring-2 ring-white'
                : 'bg-white/10'
            }`}
          >
            <div className="text-xs uppercase opacity-75">Right</div>
            <div className="font-semibold">
              {sideDurations.right > 0 ? `${sideDurations.right}m` : '--'}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {timer.type === 'FEED' && !timer.isPaused && (
          <button
            onClick={handleSwitchSide}
            className="flex-1 bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg font-medium transition-colors"
          >
            Switch to {timer.side === 'left' ? 'Right' : 'Left'}
          </button>
        )}
        <button
          onClick={handlePauseResume}
          className="bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg font-medium transition-colors"
          title={timer.isPaused ? 'Resume timer' : 'Pause timer'}
        >
          {timer.isPaused ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
        <button
          onClick={handleStopAndLog}
          className="flex-1 bg-white text-primary-600 hover:bg-primary-50 py-2 px-3 rounded-lg font-semibold transition-colors"
        >
          Stop & Log
        </button>
        <button
          onClick={handleStop}
          className="bg-white/20 hover:bg-white/30 py-2 px-3 rounded-lg transition-colors"
          title="Discard timer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
